/**
 * Utility to parse chunk IDs into numeric chapter numbers.
 * Handles both simple chunks (chunk0, dc-005) and grouped chunks (dc-001-003, dc-026-027).
 * 
 * @param {string|null|undefined} chunk - Chunk identifier (e.g., 'chunk0', 'dc-001-003', 'dq-1', etc.)
 * @returns {number|null} - Extracted chapter number (1-based), or null if cannot parse
 */
export function parseChunkToChapterNum(chunk) {
  if (!chunk || typeof chunk !== 'string') {
    return null;
  }

  const normalizedChunk = chunk.trim();

  // Handle Lays chunks like 'lays-003' where reading chapters begin at 1.
  // Content files are offset by +2, so convert 003 -> 1, 004 -> 2, etc.
  const laysMatch = normalizedChunk.match(/^lays-(\d+)$/i);
  if (laysMatch) {
    const laysChunk = parseInt(laysMatch[1], 10);
    if (Number.isNaN(laysChunk)) {
      return null;
    }
    return Math.max(laysChunk - 2, 1);
  }

  // Handle textbook chunks like 'chapter9', 'chapter09', etc.
  const chapterMatch = normalizedChunk.match(/^chapter\s*(\d+)$/i);
  if (chapterMatch) {
    const chapterNum = parseInt(chapterMatch[1], 10);
    return Number.isNaN(chapterNum) ? null : chapterNum;
  }

  // Remove common prefixes: chunk, dc-, dq-, etc.
  const stripped = normalizedChunk.replace(/^(chunk|dc-|dq-)/i, '');

  // Handle grouped chunks like '001-003' or '26-27' → take the first number
  const grouped = stripped.match(/^(\d+)-\d+$/);
  if (grouped) {
    const firstChunk = parseInt(grouped[1], 10);
    return Number.isNaN(firstChunk) ? null : firstChunk;
  }

  // Handle single numeric chunk
  const single = parseInt(stripped, 10);
  if (Number.isNaN(single)) {
    return null;
  }

  // For chunk0, chunk1, etc., return 1-based chapter number
  // For dc-001, dc-005, etc., the number is already 1-based canto
  // Adjust based on prefix if needed (chunk prefix is 0-based in most books)
  if (normalizedChunk.startsWith('chunk')) {
    return single + 1;
  }

  return single;
}
